import { ApolloError } from "@apollo/client";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import { TextFieldElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";

import { GaiaHookFormRootErrorAlert } from "~/modules/app/components/GaiaHookFormRootErrorAlert";
import GaiaHookFormSubmitButton from "~/modules/app/components/GaiaHookFormSubmitButton";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";

type ConfirmDeleteModalProps = {
    error?: ApolloError;
    onCancel: () => void;
    onSubmit: (reason: string) => Promise<void>;
    open: boolean;
};

interface ConfirmDeleteModalValues {
    reason: string;
}

export default function ConfirmDeleteModal({ error, onCancel, onSubmit, open }: ConfirmDeleteModalProps) {
    const { t } = useTranslation("mapEditor");

    const { control, handleSubmit, setError } = useForm<ConfirmDeleteModalValues>({
        values: { reason: "" },
    });

    const handleOnSubmit = async (values: ConfirmDeleteModalValues) => {
        await onSubmit(values.reason);
    };

    useApplyErrorToForm(error, setError);

    return (
        <Dialog open={open} onClose={() => onCancel()} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit(handleOnSubmit)}>
                <DialogTitle>{t("featureDetailPlannerAreaVisitList.confirmDeleteModal.title")}</DialogTitle>
                <DialogContent>
                    <Stack gap={2}>
                        <GaiaHookFormRootErrorAlert control={control} />
                        <Alert severity="warning">{t("featureDetailPlannerAreaVisitList.confirmDeleteModal.alertMessage")}</Alert>
                        <TextFieldElement
                            control={control}
                            label={t("featureDetailPlannerAreaVisitList.confirmDeleteModal.reason")}
                            rules={{ required: true }}
                            multiline
                            rows={3}
                            name="reason"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => onCancel()}>
                        {t("featureDetailPlannerAreaVisitList.confirmDeleteModal.btnCancel")}
                    </Button>
                    <GaiaHookFormSubmitButton color="error" control={control} fullWidth={false} variant="outlined">
                        {t("featureDetailPlannerAreaVisitList.confirmDeleteModal.btnSubmit")}
                    </GaiaHookFormSubmitButton>
                </DialogActions>
            </form>
        </Dialog>
    );
}
